import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { fetchMenus, fetchUseStaticMenu } from '../../../stores/reducers/menu.reducer';
import staticOptions from './options';
import './BootstrapSidebar.css';

const isPathMatch = (pathname, key, siblingKeys = []) => {
  if (pathname === `/${key}`) return true;
  if (!pathname.startsWith(`/${key}/`)) return false;
  const hasMoreSpecificSibling = siblingKeys.some(
    (sk) => sk !== key && sk.startsWith(`${key}/`) && (pathname === `/${sk}` || pathname.startsWith(`/${sk}/`))
  );
  return !hasMoreSpecificSibling;
};

const findAncestorKeys = (items, targetPath, ancestors = []) => {
  const keys = items.filter((i) => !i.children).map((i) => i.key);
  for (const item of items) {
    if (!item.children && isPathMatch(targetPath, item.key, keys)) {
      return ancestors;
    }
    if (item.children) {
      const result = findAncestorKeys(item.children, targetPath, [...ancestors, item.key]);
      if (result) return result;
    }
  }
  return null;
};

export default function BootstrapSidebar() {
  const dispatch = useDispatch();
  const user = useSelector(state => state.auth.user);
  const { menus: dbMenus, menusLoaded, useStaticMenu, useStaticMenuLoaded } = useSelector(state => state.menu);
  const location = useLocation();
  const [openMenus, setOpenMenus] = useState({});

  useEffect(() => {
    if (!useStaticMenuLoaded) {
      dispatch(fetchUseStaticMenu());
    }
  }, [dispatch, useStaticMenuLoaded]);

  useEffect(() => {
    if (useStaticMenuLoaded && !useStaticMenu && !menusLoaded) {
      dispatch(fetchMenus());
    }
  }, [dispatch, useStaticMenuLoaded, useStaticMenu, menusLoaded]);

  const dynamicOptions = useMemo(() => {
    if (useStaticMenu) return staticOptions;

    if (!dbMenus.length) return [];

    return [...dbMenus]
      .sort((a, b) => a.sort_order - b.sort_order)
      .map(menu => {
        const children = (menu.abilities || [])
          .filter(a => a.route)
          .map(a => ({
            key: a.route,
            label: a.description,
            ability: [a.ability],
          }));
        return {
          label: menu.label,
          key: menu.key,
          leftIcon: menu.icon,
          ability: (menu.abilities || []).map(a => a.ability),
          children,
        };
      })
      .filter(menu => menu.children.length > 0);
  }, [dbMenus, useStaticMenu]);

  useEffect(() => {
    const ancestors = findAncestorKeys(dynamicOptions, location.pathname);
    if (!ancestors) return;

    setOpenMenus(prev => {
      const allOpen = ancestors.every(k => prev[k]);
      if (allOpen) return prev;
      return { ...prev, ...Object.fromEntries(ancestors.map(k => [k, true])) };
    });
  }, [location.pathname, dynamicOptions]);

  const toggleMenu = (key, ancestorKeys = []) => {
    setOpenMenus(prev => {
      const next = {};
      ancestorKeys.forEach(k => { next[k] = true; });
      if (!prev[key]) {
        next[key] = true;
      }
      return next;
    });
  };

  const isItemActive = (item, siblings = []) => {
    if (!item.children) {
      const siblingKeys = siblings.filter((s) => !s.children).map((s) => s.key);
      return isPathMatch(location.pathname, item.key, siblingKeys);
    }
    return item.children.some(child => isItemActive(child, item.children));
  };

  const renderMenuItems = (items, level = 0, ancestorKeys = []) => {
    return items.map((item) => {
      const { key, label, leftIcon, children, ability } = item;

      const checkAbility = (ab) =>
        !ab || ab.length === 0 ||
        ab.some(requiredAbility => user.abilities.some(a => a.ability === requiredAbility)) ||
        user.isAdmin;

      if (!checkAbility(ability)) {
        return null;
      }

      const icon = (level === 0 && leftIcon) || null;
      const hasChildren = children && children.length > 0;
      const isOpen = openMenus[key];

      if (hasChildren) {
        const filteredChildren = children.filter((child) => checkAbility(child.ability));

        if (filteredChildren.length === 0) return null;

        const activeParent = isItemActive(item, items);

        return (
          <div key={key}>
            <a
              className={`nav-link ${isOpen ? '' : 'collapsed'} ${activeParent ? 'has-active' : ''}`}
              href="#"
              onClick={(e) => {
                e.preventDefault();
                toggleMenu(key, ancestorKeys);
              }}
              role="button"
              aria-expanded={isOpen}
            >
              <span>
                {icon && <i className={`${icon} main-icon`} />}
                {label}
              </span>
              <i className="ion-chevron-right arrow-icon" />
            </a>
            <div className={`submenu-collapse ${isOpen ? 'open' : ''}`}>
              <div className="submenu nav flex-column">
                {renderMenuItems(filteredChildren, level + 1, [...ancestorKeys, key])}
              </div>
            </div>
          </div>
        );
      } else {
        const to = `/${key}`;
        const siblingKeys = items.filter((s) => !s.children).map((s) => s.key);
        const active = isPathMatch(location.pathname, key, siblingKeys);
        return (
          <Link
            key={key}
            to={to}
            className={`nav-link ${active ? 'active' : ''}`}
            onClick={(e) => {
              if (location.pathname === to) {
                e.preventDefault();
              }
            }}
          >
            <span>
              {icon && <i className={`${icon} main-icon`} />}
              {label}
            </span>
          </Link>
        );
      }
    });
  };

  return (
    <nav className="bootstrap-sidebar-menu nav flex-column">
      {renderMenuItems(dynamicOptions)}
    </nav>
  );
}
